'use client';

import Image from 'next/image';
import { ChangeEvent, useState } from 'react';
import { useSearchParams } from 'next/navigation';

import RulerIcon from '@/public/img/icons/ruler-icon.svg';
import { squareFeetOptions, yearBuiltOptions } from './optionsData';

import { Select } from '@/components/atoms/Inputs/Select';
import { Button } from '@/components/atoms/Button';
import { InputText } from '@/components/atoms/Inputs/InputText';
import profiler from '@/helpers/userProfile';

export interface SystemSizingCalculatorProps {
	testId?: string;
}

const SystemSizingCalculator = (props: SystemSizingCalculatorProps) => {
	const { testId = 'SystemSizingCalculator' } = props;

	const params = useSearchParams();
	const squareFeetParam = params.get('squareFeet');
	const yearBuiltParam = params.get('yearBuilt');
	const zipCodeParam = params.get('zipCode');

	// ---- Taken from Repair Replace Caluclator ----
	// prevents user from selecting values that are not in the options by
	// checking if the query params are in the options, otherwise it will
	// default to an empty string. This prevents the user from navigating
	// to results with invalid values in the query params
	const getInitialValue = (param: string | null, options: string[]) =>
		param && options.includes(param) ? param : '';

	const [userProfile, updateProfile] = profiler();

	const [formData, setFormData] = useState({
		squareFeet: getInitialValue(squareFeetParam, squareFeetOptions),
		yearBuilt: getInitialValue(yearBuiltParam, yearBuiltOptions),
		zipCode: zipCodeParam || userProfile.zip,
	});

	const [squareFeetErrorText, setSquareFeetErrorText] = useState('');
	const [yearBuiltErrorText, setYearBuiltErrorText] = useState('');
	const [zipCodeErrorText, setZipCodeErrorText] = useState('');

	const { squareFeet, yearBuilt, zipCode } = formData;

	// Verify that the input is nothing else but a US Zip Code.
	const verifyZipcodeFormat = (zipCode: string) => {
		if (zipCode === '') {
			return 'Enter a valid zip code';
		}

		const isValidZip = /(^\d{5}$)/.test(zipCode);

		if (!isValidZip || zipCode === '00000') {
			return 'That doesn´t look like a valid zip code';
		}

		return false;
	};

	const missingSquareFeet = !squareFeet;
	const missingYearBuilt = !yearBuilt;
	const invalidZipCode = verifyZipcodeFormat(zipCode);

	const missingInfo = missingYearBuilt || missingSquareFeet || invalidZipCode;

	const handleChange = (
		e: ChangeEvent<HTMLSelectElement | HTMLInputElement>,
	) => {
		if (e.target.name === 'squareFeet') {
			setSquareFeetErrorText('');
		}
		if (e.target.name === 'yearBuilt') {
			setYearBuiltErrorText('');
		}
		if (e.target.name === 'zipCode') {
			setZipCodeErrorText('');
			updateProfile('zip', e.target.value);
		}

		// This will make sure that long zip codes are not accepted
		// I did it like this in case someone has autocomplete for this
		if (e.target.name === 'zipCode' && e.target.value.length > 5) {
			if (e.target.value.charAt(5) === '-') {
				e.target.value = e.target.value.slice(0, 5);
			}
		}

		setFormData((prevFormData) => ({
			...prevFormData,
			[e.target.name]: e.target.value,
		}));
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		setSquareFeetErrorText('');
		if (missingSquareFeet) {
			setSquareFeetErrorText('Select square footage');
		}
		if (missingYearBuilt) {
			setYearBuiltErrorText('Select year built');
		}
		if (typeof invalidZipCode === 'string') {
			setZipCodeErrorText(invalidZipCode);
		}
		if (missingInfo) {
			e.preventDefault();
		}
	};

	return (
		<div
			data-testid={testId}
			className="mx-auto mb-10 max-w-101 overflow-hidden rounded-2xl border-2 border-sky-600
				bg-sky-50 px-4 pb-8 pt-6 lg:mb-20 lg:px-6 lg:pb-10 lg:pt-8"
		>
			<div className="mb-6 flex justify-between lg:mb-8">
				<div className="text-sky-700">
					<div className="text-xl font-semibold lg:text-2xl">System sizing</div>
					<div className="text-sm font-semibold lg:text-base">Calculator</div>
				</div>
				<Image
					src={RulerIcon}
					alt="Ruler icon rotated 45 degrees"
					className="w-10 lg:w-12"
				/>
			</div>
			<form
				data-testid="SystemSizingCalculatorForm"
				className="flex flex-col"
				onSubmit={handleSubmit}
				action="/tools/hvac-load-calculator/results/"
			>
				<div className="mb-4">
					<Select
						id="squareFeet"
						label="Square feet"
						name="squareFeet"
						onChange={handleChange}
						value={squareFeet}
						inputProps={{ className: '!bg-white' }}
						contextRowProps={{ 'data-testid': 'squareFeetContextRow' }}
						error={squareFeetErrorText}
					>
						<option></option>
						{squareFeetOptions.map((option, i) => (
							<option key={option} value={option}>
								{i + 1 === squareFeetOptions.length
									? `${parseInt(option).toLocaleString('en-US')} or larger`
									: `${parseInt(option).toLocaleString('en-US')}`}
							</option>
						))}
					</Select>
				</div>
				<div className="mb-4">
					<Select
						id="yearBuilt"
						label="Year built"
						name="yearBuilt"
						onChange={handleChange}
						value={yearBuilt}
						inputProps={{ className: '!bg-white' }}
						contextRowProps={{ 'data-testid': 'yearBuiltContextRow' }}
						error={yearBuiltErrorText}
					>
						<option></option>
						{yearBuiltOptions.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</Select>
				</div>
				<div className="mb-6 lg:mb-8">
					<InputText
						id="zipCode"
						label={'Zip code'}
						name="zipCode"
						value={zipCode}
						onChange={handleChange}
						error={zipCodeErrorText}
					/>
				</div>
				<Button variant="large-default">Get my results</Button>
			</form>
		</div>
	);
};

export default SystemSizingCalculator;
